import { defineStore } from 'pinia';
import { store } from '/@/store';
import { to } from '@flinks/utils';
import { getStudentInfo } from '/@/api/user';
import { StudentInfoModel } from '/@/api/user/model';
import { getToken, removeToken, setToken } from '@flinks/login';
import { DEFAULT_FAVICON } from '/@/libs/config';
import { getCompanyId, getWebConfig, getWebConfigs } from '/@/api/common';
import { useFavicon, useTitle } from '@vueuse/core';
import { IResGetWebConfig } from '/@/api/common/model';
import { InviteInfo } from '/@/api/live/model';

export interface LoginModel {
  /**
   * 企业id
   */
  companyId: string;
  /**
   * 学员id
   */
  studentId: string;
  /**
   * 登录凭证
   */
  token: string;
}

interface UserState {
  /**
   * 当前登录用户信息
   */
  userInfo: Nullable<StudentInfoModel>;
  /**
   * 登录信息
   */
  token: string;
  /**
   * 企业id
   */
  companyId: string;
  /**
   * 网站标题
   */
  title: string;
  /**
   * 网站配置信息
   */
  webConfigs: IResGetWebConfig;
  /**
   * 分享配置信息
   */
  inviteLinkInfo: Nullable<InviteInfo>;
}

export const useUserStore = defineStore({
  id: 'user',
  state: (): UserState => ({
    userInfo: null,
    token: getToken() || '',
    companyId: '',
    title: '',
    webConfigs: {
      appDownloadImage: '',
      appDownloadQrcode: '',
      configId: '',
      navigationBarLogo: '',
      thePublicQrcode: '',
      openWxAuth: 0,
      icpText: '',
      isDisplayLearnerNum: 0,
      threshold: 0,
      isDisplayVipPrice: 0,
    },
    inviteLinkInfo: null,
  }),
  getters: {
    /**
     * 获取分享配置信息
     */
    getInviteLinkInfo(): Nullable<InviteInfo> {
      return this.inviteLinkInfo;
    },
    /**
     * 获取企业id
     */
    getCompanyId(): string {
      return this.companyId;
    },
    /**
     * 获取登录用户信息
     */
    getUserInfo(): StudentInfoModel {
      return (this.userInfo || {}) as StudentInfoModel;
    },
    /**
     * 获取登录用户的凭证token
     */
    getToken(): string {
      return this.token || getToken();
    },
    /**
     * 获取登录用户的凭证token
     */
    getTitle(): string {
      return this.title;
    },
    /**
     * 获取配置信息
     */
    getWebConfigs(): Nullable<IResGetWebConfig> {
      return this.webConfigs;
    },
  },

  actions: {
    /**
     * 设置分享配置信息
     */
    setInviteLinkInfo(data: InviteInfo) {
      this.inviteLinkInfo = data;
    },

    setCompanyId(id: string) {
      this.companyId = id;
    },

    /**
     * 设置用户登录信息
     *
     * @param {string} token 登录信息
     */
    setToken(token: string) {
      this.token = token;
      setToken(token);
    },

    /**
     * 清除登录信息
     */
    clearState() {
      this.token = '';
      removeToken();
    },

    /**
     * 设置登录用户信息
     *
     * @param {UserInfo} info 用户信息
     */
    setUserInfo(info: Nullable<StudentInfoModel>) {
      this.userInfo = info;
    },

    /**
     * 清除学员信息
     */
    clearUserInfo() {
      this.userInfo = null;
    },

    /**
     * 获取登录用户信息
     */
    async getUserInfoAction() {
      const [err, res] = await to(getStudentInfo());
      if (err) return;
      if (res) {
        this.setUserInfo(res);
      }
    },

    /**
     * 获取企业ID
     */
    async getCompanyIdAction(domainText: string) {
      const res = await getCompanyId({ domainText });
      this.setCompanyId(res);
    },

    /**
     * 获取网站配置信息
     */
    async getWebConfigAction() {
      if (!this.companyId) return;
      const [, res] = await to(getWebConfig({ companyId: this.companyId }));
      if (res) {
        Object.keys(res).forEach((key) => {
          res[key] = res[key]?.replace('http://', 'https://') || '';
        });
        if (res.websiteName) {
          useTitle(res.websiteName);
        }
        useFavicon(res.websiteIcon || DEFAULT_FAVICON);
      }
    },

    async getWebConfigByCompanyId() {
      if (this.webConfigs.configId) return Promise.resolve();
      const [_, res] = await to(getWebConfigs({ companyId: this.companyId }));
      await this.getWebConfigAction();
      if (!res) return Promise.resolve();
      this.webConfigs = res;
      return Promise.resolve();
    },
  },
});

/**
 * 需要在设置之外使用
 */
export function useUserStoreWithOut() {
  return useUserStore(store);
}
